import React, { useState, useEffect } from 'react';
import { holidayAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';

const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS         = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const TYPE_CFG = {
  national: { label:'National',  bg:'#fef3c7', text:'#92400e', dot:'#f59e0b', glow:'#fbbf2444' },
  regional: { label:'Regional',  bg:'#dbeafe', text:'#1e40af', dot:'#3b82f6', glow:'#3b82f644' },
  company:  { label:'Company',   bg:'#dcfce7', text:'#14532d', dot:'#22c55e', glow:'#22c55e44' },
};
const tcfg = (t) => TYPE_CFG[t] || TYPE_CFG.company;

const INP = { width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', background:'#fafbfc', transition:'border .2s' };

export default function Holidays() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();

  const [holidays, setHolidays] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [viewMonth,setViewMonth]= useState(new Date().getMonth());  // 0-11
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ name:'', date:'', type:'company', description:'', isOptional:false });
  const [saving,   setSaving]   = useState(false);
  const [view,     setView]     = useState('calendar'); // calendar | list

  useEffect(() => { load(); }, [year]);

  const load = async () => {
    setLoading(true);
    try { const r = await holidayAPI.getAll({ year }); setHolidays(r.data.data || []); }
    catch { toast.error('Failed to load holidays'); }
    setLoading(false);
  };

  const openAdd = () => {
    setEditing(null);
    const d = new Date(year, viewMonth, 1);
    setForm({ name:'', date:`${year}-${String(viewMonth+1).padStart(2,'0')}-01`, type:'company', description:'', isOptional:false });
    setModal(true);
  };
  const openEdit = (h) => {
    setEditing(h._id);
    setForm({ name:h.name, date:h.date?.slice(0,10), type:h.type||'company', description:h.description||'', isOptional:!!h.isOptional });
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await holidayAPI.update(editing, form); toast.success('Updated'); }
      else          { await holidayAPI.create(form);          toast.success('Holiday added!'); }
      setModal(false); load();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Delete this holiday?', { title: 'Delete Holiday?', variant: 'danger' });
    if (!ok) return;
    try { await holidayAPI.delete(id); setHolidays(h => h.filter(x => x._id !== id)); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  // ── Calendar helpers ─────────────────────────────────────────────────────
  const holidayMap = {};
  holidays.forEach(h => { const k = h.date?.slice(0,10); if (k) { if (!holidayMap[k]) holidayMap[k] = []; holidayMap[k].push(h); } });

  const buildCalendar = (month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const upcoming = holidays
    .filter(h => h.date >= todayStr)
    .sort((a,b) => a.date.localeCompare(b.date))
    .slice(0, 5);

  const monthHolidays = holidays.filter(h => {
    const d = new Date(h.date);
    return d.getMonth() === viewMonth && d.getFullYear() === year;
  }).sort((a,b) => a.date.localeCompare(b.date));

  const cells = buildCalendar(viewMonth);

  // ── Styles ───────────────────────────────────────────────────────────────
  const S = {
    page:  { maxWidth:1300, margin:'0 auto', fontFamily:"'DM Sans',sans-serif", paddingBottom:48 },
    card:  { background:'#fff', borderRadius:20, padding:24, boxShadow:'0 2px 20px #00000009', border:'1px solid #f0f4f8' },
    btn:   (v) => ({ padding:'9px 20px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, transition:'all .18s',
      ...(v==='primary' ? { background:'linear-gradient(135deg,#1e293b,#334155)', color:'#fff', boxShadow:'0 4px 14px #1e293b33' }
        : v==='ghost'   ? { background:'#f1f5f9', color:'#475569' }
        : v==='danger'  ? { background:'#fee2e2', color:'#dc2626' }
        :                 { background:'#fff', border:'1.5px solid #e2e8f0', color:'#475569' }) }),
    tab:   (a) => ({ padding:'8px 18px', borderRadius:9, border:'none', fontWeight:700, fontSize:12, cursor:'pointer', letterSpacing:'0.04em', textTransform:'uppercase',
      background: a ? '#1e293b' : '#f1f5f9', color: a ? '#fff' : '#64748b', transition:'all .2s' }),
  };

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin { to { transform:rotate(360deg) } }
        .hol-day:hover { background:#f8fafc!important; cursor:pointer; }
        .hol-row:hover { background:#f8fafc!important; }
        .hol-pill:hover { transform:translateY(-1px); box-shadow:0 4px 12px #0002; }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ margin:0, fontSize:32, fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>🗓 Holiday Calendar</h1>
          <p style={{ margin:'5px 0 0', color:'#94a3b8', fontSize:14 }}>{holidays.length} holidays in {year} · {upcoming.length} upcoming</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', background:'#f1f5f9', borderRadius:10, padding:3, gap:2 }}>
            <button style={S.tab(view==='calendar')} onClick={() => setView('calendar')}>📅 Calendar</button>
            <button style={S.tab(view==='list')}     onClick={() => setView('list')}>☰ List</button>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'6px 14px' }}>
            <button onClick={() => setYear(y => y-1)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#475569', lineHeight:1 }}>‹</button>
            <span style={{ fontWeight:800, fontSize:15, color:'#0f172a', minWidth:44, textAlign:'center' }}>{year}</span>
            <button onClick={() => setYear(y => y+1)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#475569', lineHeight:1 }}>›</button>
          </div>
          {isAdmin && <button style={S.btn('primary')} onClick={openAdd}>+ Add Holiday</button>}
        </div>
      </div>

      {/* ── Upcoming strip ───────────────────────────────────────────────── */}
      {upcoming.length > 0 && (
        <div style={{ display:'flex', gap:10, marginBottom:24, overflowX:'auto', paddingBottom:4 }}>
          {upcoming.map(h => {
            const cfg = tcfg(h.type);
            const d = new Date(h.date);
            const diff = Math.ceil((d - today) / 86400000);
            return (
              <div key={h._id} className="hol-pill"
                style={{ flexShrink:0, background:cfg.bg, borderRadius:14, padding:'12px 18px', minWidth:160, border:`1.5px solid ${cfg.dot}44`, transition:'all .2s', animation:'fadeUp .3s ease' }}>
                <div style={{ fontSize:11, fontWeight:700, color:cfg.text, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{cfg.label}</div>
                <div style={{ fontWeight:800, fontSize:14, color:'#0f172a', marginBottom:2 }}>{h.name}</div>
                <div style={{ fontSize:12, color:'#64748b' }}>{d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</div>
                <div style={{ fontSize:11, fontWeight:700, color:cfg.text, marginTop:6 }}>
                  {diff === 0 ? '🎉 Today!' : diff === 1 ? '⏰ Tomorrow' : `📅 In ${diff} days`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Type legend ─────────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap' }}>
        {Object.entries(TYPE_CFG).map(([type, cfg]) => (
          <div key={type} style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:'#475569' }}>
            <div style={{ width:10, height:10, borderRadius:3, background:cfg.dot }}/>
            {cfg.label}
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:'#94a3b8' }}>
          <div style={{ width:10, height:10, borderRadius:3, border:'2px dashed #cbd5e1' }}/>
          Optional
        </div>
        <div style={{ marginLeft:'auto', fontWeight:700, fontSize:13, color:'#0f172a' }}>{holidays.length} total · {holidays.filter(h=>!h.isOptional).length} mandatory</div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:80 }}>
          <div style={{ width:40, height:40, border:'3px solid #f1f5f9', borderTop:'3px solid #1e293b', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }}/>
        </div>
      ) : view === 'calendar' ? (

        /* ── CALENDAR VIEW ─────────────────────────────────────────────── */
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20 }}>

          {/* Big calendar */}
          <div style={S.card}>
            {/* Month nav */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <button onClick={() => setViewMonth(m => { if(m===0){setYear(y=>y-1);return 11;} return m-1; })}
                style={{ ...S.btn(), padding:'8px 14px', fontSize:16 }}>‹</button>
              <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:'#0f172a' }}>{MONTHS_LONG[viewMonth]} {year}</h2>
              <button onClick={() => setViewMonth(m => { if(m===11){setYear(y=>y+1);return 0;} return m+1; })}
                style={{ ...S.btn(), padding:'8px 14px', fontSize:16 }}>›</button>
            </div>

            {/* Day headers */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', padding:'6px 0', letterSpacing:'0.06em' }}>{d}</div>
              ))}
            </div>

            {/* Date grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
              {cells.map((day, i) => {
                if (!day) return <div key={`e-${i}`}/>;
                const dateStr = `${year}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const dayHols = holidayMap[dateStr] || [];
                const isToday = dateStr === todayStr;
                const isWeekend = [0,6].includes(new Date(year, viewMonth, day).getDay());
                return (
                  <div key={day} className="hol-day"
                    onClick={() => { if(isAdmin && dayHols.length===0){ setForm({...form, date:dateStr}); setEditing(null); setModal(true); } }}
                    style={{ minHeight:72, borderRadius:12, padding:'8px 6px', border:`1.5px solid ${isToday ? '#1e293b' : dayHols.length > 0 ? tcfg(dayHols[0].type).dot+'55' : '#f0f4f8'}`,
                      background: isToday ? '#1e293b' : dayHols.length > 0 ? tcfg(dayHols[0].type).bg : isWeekend ? '#fafbfc' : '#fff',
                      position:'relative', transition:'all .15s' }}>
                    <div style={{ fontWeight: isToday ? 800 : isWeekend ? 600 : 500, fontSize:13, color: isToday ? '#fff' : dayHols.length > 0 ? tcfg(dayHols[0].type).text : isWeekend ? '#94a3b8' : '#374151', marginBottom:4 }}>{day}</div>
                    {dayHols.map(h => (
                      <div key={h._id} title={h.name}
                        style={{ fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:5, background: tcfg(h.type).dot, color:'#fff', marginBottom:2,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', border: h.isOptional ? '1.5px dashed rgba(255,255,255,0.6)' : 'none',
                          cursor: isAdmin ? 'pointer' : 'default' }}
                        onClick={e => { e.stopPropagation(); isAdmin && openEdit(h); }}>
                        {h.name}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar: this month's holidays */}
          <div>
            <div style={{ ...S.card, marginBottom:16 }}>
              <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:800, color:'#0f172a' }}>
                {MONTHS_SHORT[viewMonth]} Holidays
                <span style={{ marginLeft:8, fontSize:12, fontWeight:600, color:'#94a3b8' }}>({monthHolidays.length})</span>
              </h3>
              {monthHolidays.length === 0 ? (
                <div style={{ textAlign:'center', padding:'24px 0', color:'#cbd5e1' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>🌿</div>
                  <div style={{ fontSize:13 }}>No holidays this month</div>
                  {isAdmin && <button style={{ ...S.btn('ghost'), marginTop:12, fontSize:12 }} onClick={openAdd}>+ Add one</button>}
                </div>
              ) : monthHolidays.map(h => {
                const cfg = tcfg(h.type);
                const d = new Date(h.date);
                return (
                  <div key={h._id} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
                    <div style={{ width:44, height:44, borderRadius:10, background:cfg.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <div style={{ fontSize:9, fontWeight:800, color:cfg.text, textTransform:'uppercase' }}>{DAYS[d.getDay()]}</div>
                      <div style={{ fontSize:18, fontWeight:900, color:cfg.text, lineHeight:1 }}>{d.getDate()}</div>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#0f172a', marginBottom:2 }}>{h.name}</div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:cfg.bg, color:cfg.text, fontWeight:700 }}>{cfg.label}</span>
                        {h.isOptional && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#f1f5f9', color:'#94a3b8', border:'1px dashed #cbd5e1' }}>Optional</span>}
                      </div>
                    </div>
                    {isAdmin && (
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <button onClick={() => openEdit(h)} style={{ ...S.btn('ghost'), padding:'4px 8px', fontSize:11 }}>✏️</button>
                        <button onClick={() => handleDelete(h._id)} style={{ ...S.btn('danger'), padding:'4px 8px', fontSize:11 }}>🗑</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Year summary mini */}
            <div style={S.card}>
              <h3 style={{ margin:'0 0 14px', fontSize:13, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Year at a Glance</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:4 }}>
                {MONTHS_SHORT.map((m, mi) => {
                  const count = holidays.filter(h => new Date(h.date).getMonth() === mi).length;
                  const isCur = mi === viewMonth;
                  return (
                    <button key={m} onClick={() => setViewMonth(mi)}
                      style={{ padding:'8px 4px', borderRadius:8, border:`1.5px solid ${isCur ? '#1e293b' : count > 0 ? '#e2e8f0' : 'transparent'}`,
                        background: isCur ? '#1e293b' : count > 0 ? '#f8fafc' : 'transparent',
                        cursor:'pointer', textAlign:'center', transition:'all .15s' }}>
                      <div style={{ fontSize:9, fontWeight:700, color: isCur ? '#fff' : '#94a3b8', textTransform:'uppercase' }}>{m}</div>
                      {count > 0 && <div style={{ fontSize:11, fontWeight:800, color: isCur ? '#fff' : '#f59e0b', marginTop:2 }}>{count}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

      ) : (
        /* ── LIST VIEW ─────────────────────────────────────────────────── */
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>All Holidays {year}</h3>
            {isAdmin && <button style={S.btn('primary')} onClick={openAdd}>+ Add Holiday</button>}
          </div>
          {holidays.length === 0 ? (
            <div style={{ textAlign:'center', padding:48, color:'#94a3b8' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
              <div style={{ fontWeight:700, fontSize:16 }}>No holidays for {year}</div>
              {isAdmin && <button style={{ ...S.btn('primary'), marginTop:16 }} onClick={openAdd}>Add First Holiday</button>}
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Date','Day','Holiday','Type','Description','Optional',''].map(h=>(
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'2px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...holidays].sort((a,b)=>a.date.localeCompare(b.date)).map((h,i) => {
                  const cfg = tcfg(h.type);
                  const d = new Date(h.date);
                  const isPast = h.date < todayStr;
                  return (
                    <tr key={h._id} className="hol-row" style={{ background: i%2===0?'#fff':'#fafbfc', opacity: isPast ? 0.6 : 1, transition:'background .15s' }}>
                      <td style={{ padding:'12px 14px', fontWeight:700, color:'#0f172a', fontFamily:"'DM Mono',monospace", fontSize:12 }}>{d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</td>
                      <td style={{ padding:'12px 14px', color:'#64748b' }}>{DAYS[d.getDay()]}</td>
                      <td style={{ padding:'12px 14px', fontWeight:700, color:'#0f172a' }}>{h.name}</td>
                      <td style={{ padding:'12px 14px' }}>
                        <span style={{ padding:'3px 10px', borderRadius:8, fontSize:11, fontWeight:700, background:cfg.bg, color:cfg.text }}>{cfg.label}</span>
                      </td>
                      <td style={{ padding:'12px 14px', color:'#64748b', maxWidth:200 }}>{h.description || '—'}</td>
                      <td style={{ padding:'12px 14px' }}>
                        {h.isOptional ? <span style={{ fontSize:18 }}>✓</span> : <span style={{ color:'#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ padding:'12px 14px' }}>
                        {isAdmin && (
                          <div style={{ display:'flex', gap:6 }}>
                            <button onClick={() => openEdit(h)} style={{ ...S.btn('ghost'), padding:'5px 10px', fontSize:12 }}>Edit</button>
                            <button onClick={() => handleDelete(h._id)} style={{ ...S.btn('danger'), padding:'5px 10px', fontSize:12 }}>Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(3px)' }}
          onClick={() => setModal(false)}>
          <div style={{ background:'#fff', borderRadius:22, padding:32, width:460, maxWidth:'93vw', boxShadow:'0 24px 80px #0003', animation:'fadeUp .2s ease' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 22px', fontSize:20, fontWeight:800, color:'#0f172a' }}>{editing ? '✏️ Edit Holiday' : '+ New Holiday'}</h3>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Name *</label>
                <input style={INP} required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Diwali, Republic Day"/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Date *</label>
                  <input style={INP} type="date" required value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Type</label>
                  <select style={INP} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                    <option value="national">National</option>
                    <option value="regional">Regional</option>
                    <option value="company">Company</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Description</label>
                <textarea style={{ ...INP, resize:'vertical' }} rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Optional note..."/>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', padding:'10px 14px', background:'#f8fafc', borderRadius:10, border:'1.5px solid #e2e8f0' }}>
                <input type="checkbox" checked={form.isOptional} onChange={e=>setForm(f=>({...f,isOptional:e.target.checked}))} style={{ width:16, height:16, cursor:'pointer' }}/>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>Optional Holiday</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>Employees choose whether to take this day off</div>
                </div>
              </label>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button type="button" onClick={()=>setModal(false)} style={{ ...S.btn('ghost'), flex:1, justifyContent:'center' }}>Cancel</button>
                <button type="submit" style={{ ...S.btn('primary'), flex:1, justifyContent:'center' }} disabled={saving}>
                  {saving ? 'Saving…' : (editing ? 'Update' : 'Add Holiday')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
